"use client"

import Link from "next/link"
import Image from "next/image"
import { ArrowRight, Calendar } from "lucide-react"
import { motion } from "motion/react"

import { cn } from "@/lib/utils"
import { articles } from "@/lib/articles"
import { Badge } from "@/components/ui/badge"
import VerticalCutReveal from "@/components/ui/fancy/vertical-cut-reveal"

const FALLBACK_IMAGE = "/images/remote/Adis95-1024x683.jpg"

function formatDate(dateStr: string) {
  const d = new Date(dateStr)
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

const recentArticles = [...articles]
  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  .slice(0, 3)

export function NewsGrid() {
  return (
    <section className="section-normal px-4 md:px-6">
      <div className="mx-auto max-w-7xl">
        {/* Section heading */}
        <div className="mb-12">
          <Badge variant="secondary" className="mb-4">
            {"Actualités"}
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl" style={{ textWrap: "balance" }}>
            <VerticalCutReveal
              splitBy="words"
              staggerDuration={0.06}
              staggerFrom="first"
              transition={{ type: "spring", stiffness: 220, damping: 24 }}
            >
              Nos actualités
            </VerticalCutReveal>
          </h2>
        </div>

        {/* Asymmetric news grid */}
        <motion.div
          className="grid grid-cols-1 gap-6 md:grid-cols-3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ staggerChildren: 0.1 }}
        >
          {recentArticles.map((article, index) => {
            const isFeatured = index === 0

            return (
              <motion.article
                key={article.slug}
                variants={{
                  hidden: { opacity: 0, y: 14 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.5, ease: [0.25, 0.4, 0, 1] }}
                className={cn(
                  "group",
                  isFeatured && "md:col-span-2 md:row-span-2",
                  "rounded-[2rem] p-1.5",
                  "bg-foreground/[0.02] ring-1 ring-foreground/5",
                  "dark:bg-background/[0.06] dark:ring-foreground/10",
                  "transition-[box-shadow,ring-color,transform] duration-300 ease-[var(--ease-premium)] hover:shadow-[var(--shadow-md)] active:scale-[0.98]"
                )}
              >
                <div
                  className={cn(
                    "flex h-full flex-col rounded-[calc(2rem-0.375rem)] bg-card overflow-hidden",
                  )}
                >
                  {/* Article image */}
                  <div className={cn(
                    "relative w-full overflow-hidden",
                    isFeatured ? "h-64" : "h-48"
                  )}>
                    <Image
                      src={article.imageUrl ?? FALLBACK_IMAGE}
                      alt={article.title}
                      fill
                      className="object-cover transition-[transform] duration-300 ease-[var(--ease-premium)] group-hover:scale-105"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                  </div>

                  <div className={cn(
                    "flex flex-1 flex-col",
                    isFeatured ? "p-8 md:p-10" : "p-6 md:p-8"
                  )}>
                  <div className="flex flex-wrap items-center gap-3">
                    <Badge variant="secondary">
                      {article.category === "news" ? "Actualité" : "Promotion"}
                    </Badge>
                    <time dateTime={article.date} className="published-date flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Calendar className="size-3" />
                      {formatDate(article.date)}
                    </time>
                    <span className="author text-xs text-muted-foreground">ADIS 95</span>
                  </div>

                  <h3
                    className={cn(
                      "mt-4 font-semibold tracking-tight",
                      isFeatured ? "text-2xl" : "text-lg"
                    )}
                  >
                    {article.title}
                  </h3>

                  <p
                    className={cn(
                      "mt-3 leading-relaxed text-muted-foreground",
                      isFeatured ? "max-w-[50ch] text-lg" : "text-sm"
                    )}
                  >
                    {article.excerpt}
                  </p>

                  <div className="mt-auto pt-6">
                    <Link
                      href={`/actualites/${article.slug}`}
                      className={cn(
                        "group/link inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-primary",
                        "transition-[color] duration-200 ease-[var(--ease-out-expo)] hover:text-primary/80"
                      )}
                    >
                      Lire&nbsp;: {article.title.length > 35 ? article.title.slice(0, 35).trim() + "…" : article.title}
                      <ArrowRight className="size-4 transition-[transform] duration-200 ease-[var(--ease-out-expo)] group-hover/link:translate-x-0.5" />
                    </Link>
                  </div>
                  </div>
                </div>
              </motion.article>
            )
          })}
        </motion.div>

        {/* View all link */}
        <div className="mt-10 text-center">
          <Link
            href="/actualites"
            className={cn(
              "group/cta inline-flex min-h-[44px] items-center gap-2 rounded-full border border-primary/20 px-6 py-2.5 text-sm font-medium text-primary",
              "transition-[background-color,transform] duration-200 ease-[var(--ease-out-expo)] hover:bg-primary/5 active:scale-[0.97]"
            )}
          >
            {"Voir toutes les actualités"}
            <ArrowRight className="size-4 transition-[transform] duration-200 ease-[var(--ease-out-expo)] group-hover/cta:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>
  )
}
